<template>
    <div class="h-full flex flex-col space-y-4">
        <h1 class="shrink-0 text-2xl font-semibold text-gray-700">Calls</h1>

        <div class="flex shrink-0 items-center justify-between gap-2 border-b border-gray-200">
            <SavedViewTabs
                ref="viewTabs"
                page="calls"
                :current-filter="filters"
                :can-manage="true"
                @apply="onApplyView"
            />
            <div class="flex items-center gap-2 pb-2">
                <div id="view-save-slot" class="flex items-center" />
                <OverlayFiltersFilterMenu :filters="filters" @update:filters="onFiltersChange" />
                <UInput
                    v-model="search"
                    type="text"
                    placeholder="Search..."
                    class="w-72"
                    icon="i-ph-magnifying-glass-duotone"
                />
            </div>
        </div>

        <FilterChips :filters="filters" @update:filters="onFiltersChange" class="shrink-0" />

        <div class="flex-1 bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
            <UTable
                :rows="calls"
                :columns="columns"
                :loading="loading"
                :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
                :empty-state="{ icon: 'i-ph-phone-x-duotone', label: 'No calls.' }"
                :ui="{ thead: 'sticky top-0 bg-white z-10 shadow-sm', tbody: 'divide-y divide-gray-200', tr: { base: 'cursor-pointer hover:bg-gray-50' } }"
                class="h-full"
                @select="(row: Call) => navigateTo(`/calls/${row.id}`)"
            >
                <template #id-data="{ row }">
                    <ULink :to="`/calls/${row.id}`" class="font-mono hover:underline">
                        {{ row.number.toString().padStart(8, '0') }}
                    </ULink>
                </template>
                <template #customer-data="{ row }">
                    <div class="leading-5">
                        <p>{{ [row.customer?.first_name, row.customer?.middle_name, row.customer?.last_name].filter(Boolean).join(' ') }}</p>
                        <span class="text-sm capitalize">{{ [row.customer?.gender, age(row.customer?.birth_date), row.customer?.blood_type].filter(Boolean).join(' · ') }}</span>
                    </div>
                </template>
                <template #user-data="{ row }">
                    <div v-if="row.user" class="flex items-center space-x-2">
                        <BadgeUserRole minimal :role="row.user.role" />
                        <span>{{ row.user.name }}</span>
                    </div>
                    <span v-else class="text-gray-300">–</span>
                </template>
                <template #partner-data="{ row }">
                    <div v-if="row.customer?.subscriptions?.length" class="flex items-center space-x-2.5">
                        <Avatar bucket="media" folder="partners" size="sm" :placeholder="row.customer.subscriptions[0].partner.name" :path="row.customer.subscriptions[0].partner.avatar" />
                        <div class="flex flex-col leading-tight min-w-0">
                            <span>{{ row.customer.subscriptions[0].partner.name }}</span>
                            <span v-if="row.customer.policy_holder" class="text-xs text-gray-500 truncate max-w-[40ch]" :title="row.customer.policy_holder">{{ row.customer.policy_holder }}</span>
                        </div>
                    </div>
                    <span v-else class="text-gray-300">–</span>
                </template>
                <template #status-data="{ row }">
                    <BadgeCallStatus :status="row.status" />
                </template>
                <template #feedback-data="{ row }">
                    <div v-if="row.metadata?.feedback" class="flex items-center gap-1.5">
                        <div class="flex">
                            <UIcon
                                v-for="star in 5"
                                :key="star"
                                :name="star <= row.metadata.feedback.rating ? 'i-ph-star-fill' : 'i-ph-star'"
                                :class="star <= row.metadata.feedback.rating ? 'text-yellow-500' : 'text-gray-300'"
                                class="size-4"
                            />
                        </div>
                        <UIcon
                            v-if="row.metadata.feedback.comment"
                            name="i-ph-note-fill"
                            class="text-gray-500"
                            size="12"
                        />
                    </div>
                    <span v-else class="text-gray-300">–</span>
                </template>
                <template #location-data="{ row }">
                    <div v-if="row.address" class="leading-5 max-w-36">
                        <p v-if="row.address.city || row.address.region" class="truncate">{{ [row.address.city, row.address.region].filter(Boolean).join(', ') }}</p>
                        <p class="text-sm truncate">{{ row.address.country }}</p>
                    </div>
                    <span v-else class="text-gray-300">–</span>
                </template>
                <template #tta-data="{ row }">
                    <span v-if="row.connected_at">{{ duration(row.created_at, row.connected_at) }}</span>
                    <span v-else class="text-gray-300">–</span>
                </template>
                <template #duration-data="{ row }">
                    <span v-if="row.ended_at">{{ duration(row.created_at, row.ended_at) }}</span>
                    <span v-else class="text-gray-300">–</span>
                </template>
                <template #reports-data="{ row }">
                    <TableReportsCell :reports="row.call_reports" />
                </template>
                <template #decision-data="{ row }">
                    <div v-if="row.final_decision?.[0]?.decision" class="leading-5 max-w-44">
                        <p class="truncate" :title="row.final_decision[0].decision.label">{{ row.final_decision[0].decision.label }}</p>
                        <p v-if="row.final_decision[0].decision.description" class="text-sm text-gray-500 truncate" :title="row.final_decision[0].decision.description">{{ row.final_decision[0].decision.description }}</p>
                    </div>
                    <span v-else class="text-gray-300">–</span>
                </template>
                <template #source-data="{ row }">
                    <BadgeCallSource :source="row.source" />
                </template>
                <template #created-data="{ row }">
                    <TableDateCell :date="row.created_at" />
                </template>
            </UTable>
        </div>

        <TablePagination :pagination="pagination" class="shrink-0" />
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { useStorage } from '@vueuse/core'
import { watchDebounced } from '@vueuse/core'
import { type Call } from '~/types/call'
import { UserRole } from '~/types/user'
import type { ListingStorage } from '~/types/common'
import { type Filter, FilterType } from '~/types/filter'

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.admin, UserRole.medicalDirector, UserRole.doctor, UserRole.operator] })
useHead({ title: 'Calls' })

const auth = useAuthStore()
const client = useSupabaseClient()
const pagination = usePagination(50, { syncQuery: true })

const storage = useStorage('calls-list', {} as ListingStorage)

const { age, duration } = useNumber()

const initialized = ref(false)
const loading = ref(false)
const search = ref('')
const calls = ref<Call[]>([])
const suppressReset = ref(false)

function defaultCallsFilters(): Filter[] {
    return [
        { key: FilterType.user, label: 'User' },
        { key: FilterType.callSource, label: 'Call Source' },
        { key: FilterType.callStatus, label: 'Call Status' },
        { key: FilterType.date, label: 'Date' },
        { key: FilterType.customerTag, label: 'Customer Tag' },
        { key: FilterType.partner, label: 'Partner' },
        { key: FilterType.policyHolder, label: 'Policy Holder' },
        { key: FilterType.callDecision, label: 'Final Decision' },
    ]
}

const filters = ref<Filter[]>(defaultCallsFilters())

function onApplyView(snapshot: any, initial = false) {
    if (initial) suppressReset.value = true
    const saved: Filter[] = Array.isArray(snapshot) ? snapshot : []
    filters.value = defaultCallsFilters().map(f => saved.find(s => s.key === f.key) ?? f)
}

function onFiltersChange(next: Filter[]) {
    filters.value = next
}

const columns = computed(() => [
    { key: 'id', label: 'ID' },
    { key: 'customer', label: 'Customer' },
    { key: 'user', label: 'User' },
    { key: 'partner', label: 'Partner' },
    { key: 'status', label: 'Status' },
    { key: 'created', label: 'Created' },
    { key: 'tta', label: 'TTA' },
    { key: 'duration', label: 'Duration' },
    ...((auth.hasRole(UserRole.admin) || auth.hasRole(UserRole.medicalDirector)) ? [{ key: 'feedback', label: 'Feedback' }] : []),
    { key: 'reports', label: 'Reports' },
    { key: 'decision', label: 'Decision' },
    { key: 'location', label: 'Location' },
    { key: 'source', label: 'Source' },
])

onMounted(() => {
    if (storage.value.search) {
        search.value = storage.value.search
    }

    fetchData()

    nextTick(() => initialized.value = true)

    client.channel('calls')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'calls' }, () => fetchData(true))
        .subscribe()
})

watchDebounced(search, (val) => {
    if (initialized.value) {
        fetchData(false, true)
        storage.value.search = val
    }
}, { debounce: 600 })

watchDebounced(filters, () => {
    if (!initialized.value) return
    if (suppressReset.value) {
        suppressReset.value = false
        fetchData(false, false)
    } else {
        fetchData(false, true)
    }
}, { deep: true, debounce: 300 })

watch(() => pagination.page, () => {
    if (initialized.value) {
        fetchData()
    }
})

async function fetchData(silent?: boolean, resetPagination?: boolean) {
    if (resetPagination && pagination.reset()) {
        return
    }

    if (!silent) {
        loading.value = true
        calls.value = []
        pagination.setTotal(undefined)
    }

    const filterValues = Object.fromEntries(filters.value.map(({ key, value }) => [key, value]))

    const finalDecisionEmbed = filterValues[FilterType.callDecision]?.length
        ? 'final_decision:call_final_decisions!inner (call_decision_id, decided_by, decision:options (id, label, description))'
        : 'final_decision:call_final_decisions (call_decision_id, decided_by, decision:options (id, label, description))'

    const selectStatement = (filterValues[FilterType.partner]?.length || filterValues[FilterType.customerTag]?.length || filterValues[FilterType.policyHolder]?.length)
        ? `
            id, number, source, status, created_at, connected_at, ended_at, metadata,
            customer:customers!inner (
                id, first_name, middle_name, last_name, birth_date, gender, blood_type, policy_holder,
                subscriptions!inner (
                    id,
                    partner_id,
                    is_active,
                    expires_at,
                    partner:partners!inner (id, name, avatar)
                )
            ),
            call_reports (id, call_reason_id),
            ${finalDecisionEmbed},
            address:addresses (id, line_1, city, region, country),
            user:users!calls_user_id_fkey (id, name, role),
            cus:customers ()
        ` : `
            id, number, source, status, created_at, connected_at, ended_at, metadata,
            customer:customers (
                id, first_name, middle_name, last_name, birth_date, gender, blood_type, policy_holder,
                subscriptions (
                    id,
                    partner_id,
                    is_active,
                    expires_at,
                    partner:partners (id, name, avatar)
                )
            ),
            call_reports (id, call_reason_id),
            ${finalDecisionEmbed},
            address:addresses (id, line_1, city, region, country),
            user:users!calls_user_id_fkey (id, name, role),
            cus:customers ()
        `

    let query = client.from('calls')
        .select(selectStatement, { count: 'exact' })
        .is('customers.subscriptions.is_active', true)
        .limit(1, { referencedTable: 'customers.subscriptions' })
        .order('number', { ascending: false })
        .range(pagination.from, pagination.to)

    if (search.value) {
        const searchQuery = search.value.trim()
        const parsed = parseInt(search.value)
        const isNumeric = !isNaN(parsed)
        const isValidId = isNumeric && parsed <= 2147483647

        query = query
            .or([
                'cus.not.is.null',
                isValidId ? `number.eq.${parsed}` : undefined
            ].filter(Boolean).join(','))
            .or([
                `fts.plfts(simple).'${searchQuery}'`,
                `email.ilike.%${searchQuery}%`,
                isNumeric ? `phone.ilike.%${parsed}` : undefined,
            ].filter(Boolean).join(','), { referencedTable: 'cus' })
    }

    if (filterValues[FilterType.user]) {
        query = query.eq('user_id', filterValues[FilterType.user])
    }
    if (filterValues[FilterType.callSource]?.length) {
        query = query.in('source', filterValues[FilterType.callSource])
    }
    if (filterValues[FilterType.callStatus]?.length) {
        query = query.in('status', filterValues[FilterType.callStatus])
    }
    if (filterValues[FilterType.date]?.start) {
        query = query.gte('created_at', dayjs(filterValues[FilterType.date].start).startOf('day').format())
    }
    if (filterValues[FilterType.date]?.end) {
        query = query.lte('created_at', dayjs(filterValues[FilterType.date].end).endOf('day').format())
    }
    if (filterValues[FilterType.partner]?.length) {
        query = query.in('customers.subscriptions.partner_id', filterValues[FilterType.partner])
    }
    if (filterValues[FilterType.policyHolder]?.length) {
        query = query.in('customers.policy_holder', filterValues[FilterType.policyHolder])
    }
    if (filterValues[FilterType.customerTag]?.length) {
        query = query.overlaps('customers.tags', filterValues[FilterType.customerTag])
    }
    if (filterValues[FilterType.callDecision]?.length) {
        query = query.in('final_decision.call_decision_id', filterValues[FilterType.callDecision])
    }

    const { count, data } = await query

    if (count && data) {
        if (silent) {
            calls.value = []
            pagination.setTotal(undefined)
        }

        calls.value = data as unknown as Call[]
        pagination.setTotal(count)
    }
    
    loading.value = false
}
</script>
